import { CalendarRange } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { cn } from '../../lib/shadcn/utils'
import type { PerformanceFilters, Period } from '../../utils/performance'

const PERIODS: { value: Period; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'total', label: 'Total' },
]

type Props = {
  filters: PerformanceFilters
  managers: string[]
  vehicles: string[]
  onChange: (next: PerformanceFilters) => void
}

export function PerformanceFilters({ filters, managers, vehicles, onChange }: Props) {
  return (
    <div className="flex flex-wrap items-end gap-4 border-b border-border px-4 py-3">
      {/* Date range */}
      <div className="flex items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor="perf-from" className="text-[11px] text-muted-foreground">
            From
          </Label>
          <Input
            id="perf-from"
            type="date"
            value={filters.from ?? ''}
            onChange={(e) => onChange({ ...filters, from: e.target.value || null })}
            className="h-9 w-[150px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="perf-to" className="text-[11px] text-muted-foreground">
            To
          </Label>
          <Input
            id="perf-to"
            type="date"
            value={filters.to ?? ''}
            onChange={(e) => onChange({ ...filters, to: e.target.value || null })}
            className="h-9 w-[150px]"
          />
        </div>
      </div>

      {/* Manager */}
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Manager</Label>
        <Select
          value={filters.manager}
          onValueChange={(v) => onChange({ ...filters, manager: v })}
        >
          <SelectTrigger className="h-9 w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All</SelectItem>
            <SelectItem value="Unassigned">Unassigned</SelectItem>
            {managers.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vehicle */}
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Vehicle</Label>
        <Select
          value={filters.vehicle}
          onValueChange={(v) => onChange({ ...filters, vehicle: v })}
        >
          <SelectTrigger className="h-9 w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All</SelectItem>
            {vehicles.map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Period segmented control */}
      <div className="ml-auto space-y-1">
        <Label className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <CalendarRange className="h-3 w-3" /> Trend grouping
        </Label>
        <div className="inline-flex rounded-md border border-border bg-background p-0.5">
          {PERIODS.map((p) => {
            const active = filters.period === p.value
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => onChange({ ...filters, period: p.value })}
                className={cn(
                  'rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                  active
                    ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {p.label}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
